import { useCallback, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { BaseballField } from "@/components/field/BaseballField";
import { OptionButton } from "@/components/scenario/OptionButton";
import { ScenarioCard } from "@/components/scenario/ScenarioCard";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { ErrorMessage } from "@/components/ui/ErrorMessage";
import { LoadingSpinner } from "@/components/ui/LoadingSpinner";
import { useAuth } from "@/contexts/auth-context";
import { useChallenge, useSubmitChallengeResult } from "@/hooks/use-challenges";
import { getErrorMessage } from "@/utils/error-message";

export function ChallengePlay() {
  const { challengeId } = useParams<{ challengeId: string }>();
  const { user } = useAuth();
  const { data, isLoading, error } = useChallenge(challengeId ?? "");
  const submitResult = useSubmitChallengeResult();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedOptionId, setSelectedOptionId] = useState<string | null>(null);
  const [answers, setAnswers] = useState<{ scenarioId: string; optionId: string }[]>([]);
  const [finished, setFinished] = useState(false);

  const handleSelect = useCallback((optionId: string) => {
    setSelectedOptionId(optionId);
  }, []);

  const handleConfirm = useCallback(() => {
    if (!selectedOptionId || !data) return;
    const scenario = data.scenarios[currentIndex];

    const newAnswers = [...answers, { scenarioId: scenario.scenarioId, optionId: selectedOptionId }];
    setAnswers(newAnswers);
    setSelectedOptionId(null);

    if (currentIndex + 1 < data.scenarios.length) {
      setCurrentIndex(currentIndex + 1);
    } else {
      setFinished(true);
    }
  }, [selectedOptionId, data, currentIndex, answers]);

  const handleSubmitResults = useCallback(async () => {
    if (!data || !challengeId) return;
    await submitResult.mutateAsync({
      challengeId,
      answers: answers.map((a) => ({ scenarioId: a.scenarioId, selectedOptionId: a.optionId })),
    });
  }, [data, challengeId, answers, submitResult]);

  if (isLoading) return <LoadingSpinner message="Loading challenge..." />;
  if (error) return <ErrorMessage message={getErrorMessage(error)} />;
  if (!data) return <ErrorMessage message="Challenge not found" />;

  const myResult = data.challenge.results[user?.email ?? ""];
  if (myResult) {
    const resultEntries = Object.values(data.challenge.results).sort(
      (a, b) => b.correct - a.correct,
    );

    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-navy-700">Challenge Results</h1>
        <Card>
          <p className="mb-2 text-sm text-gray-500">
            Created by {data.challenge.creatorName}
          </p>
          <div className="space-y-2">
            {resultEntries.map((r, i) => (
              <div
                key={r.userId}
                className={`flex items-center justify-between rounded-lg px-4 py-3 ${
                  i === 0 ? "bg-amber-50 border-2 border-amber-200" : "bg-gray-50"
                }`}
              >
                <div className="flex items-center gap-2">
                  {i === 0 && <span className="text-lg">{"\uD83C\uDFC6"}</span>}
                  <span className={`text-sm font-medium ${r.userId === user?.email ? "text-navy-700" : "text-gray-900"}`}>
                    {r.displayName}{r.userId === user?.email ? " (you)" : ""}
                  </span>
                </div>
                <span className="text-sm font-bold text-gray-700">
                  {r.correct}/{r.total}
                </span>
              </div>
            ))}
          </div>
        </Card>
        <Link to="/challenges">
          <Button variant="secondary" className="w-full">Back to Challenges</Button>
        </Link>
      </div>
    );
  }

  if (finished) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-navy-700">Challenge Complete!</h1>
        <Card className="text-center">
          <p className="text-lg font-bold text-gray-900">
            You answered {answers.length} of {data.scenarios.length} questions
          </p>
          {submitResult.error && <ErrorMessage message={getErrorMessage(submitResult.error)} />}
          <Button
            onClick={handleSubmitResults}
            className="mt-4 w-full"
            disabled={submitResult.isPending}
          >
            {submitResult.isPending ? "Submitting..." : "Submit Results"}
          </Button>
        </Card>
      </div>
    );
  }

  const scenario = data.scenarios[currentIndex];
  if (!scenario) return <ErrorMessage message="No scenarios in this challenge" />;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-bold text-navy-700">
          Challenge: {currentIndex + 1}/{data.scenarios.length}
        </h1>
        <span className="text-sm text-gray-400">by {data.challenge.creatorName}</span>
      </div>

      <div className="overflow-hidden rounded-xl shadow-sm">
        <BaseballField fieldState={scenario.fieldState} className="block w-full" />
      </div>

      <ScenarioCard scenario={scenario}>
        {scenario.options.map((option) => (
          <OptionButton
            key={option.id}
            label={option.text}
            state={option.id === selectedOptionId ? "selected" : "default"}
            disabled={false}
            onClick={() => handleSelect(option.id)}
          />
        ))}
      </ScenarioCard>

      <Button
        onClick={handleConfirm}
        disabled={!selectedOptionId}
        className="w-full"
        size="lg"
      >
        {currentIndex + 1 < data.scenarios.length ? "Next Question" : "Finish Challenge"}
      </Button>
    </div>
  );
}
